// Data Classification Hub Utilities
import {
  UploadFile,
  UploadStatus,
  CloudFile,
  CloudFolder,
  CloudItem
} from "../models";

export class FileUtils {
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  }

  static isValidFileType(file: File, allowedTypes: readonly string[]): boolean {
    const extension = "." + file.name.split(".").pop()?.toLowerCase();
    return allowedTypes.includes(extension);
  }

  static generateFileId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  static createUploadFile(file: File): UploadFile {
    return {
      id: this.generateFileId(),
      name: file.name,
      size: file.size,
      type: file.type,
      status: UploadStatus.PENDING,
      file
    };
  }
}

export class CloudUtils {
  static getMimeType(fileName: string): string {
    const extension = fileName.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "pdf":
        return "application/pdf";
      case "doc":
        return "application/msword";
      case "docx":
        return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      case "jpg":
      case "jpeg":
        return "image/jpeg";
      case "png":
        return "image/png";
      case "txt":
        return "text/plain";
      case "xls":
        return "application/vnd.ms-excel";
      case "xlsx":
        return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      default:
        return "application/octet-stream";
    }
  }

  static getMockCloudItems(path: string[]): CloudItem[] {
    if (path.length === 0) {
      // Root level - show month folders
      return [
        {
          id: "nov2024",
          name: "November 2024",
          fileCount: 45,
          type: "folder",
          path: "November 2024"
        },
        {
          id: "oct2024",
          name: "October 2024",
          fileCount: 67,
          type: "folder",
          path: "October 2024"
        },
        {
          id: "sep2024",
          name: "September 2024",
          fileCount: 52,
          type: "folder",
          path: "September 2024"
        },
        {
          id: "aug2024",
          name: "August 2024",
          fileCount: 38,
          type: "folder",
          path: "August 2024"
        },
        {
          id: "jul2024",
          name: "July 2024",
          fileCount: 29,
          type: "folder",
          path: "July 2024"
        }
      ];
    } else {
      return [
        {
          id: "f1",
          name: "service_contract_2024_ABC.pdf",
          size: "2.3 MB",
          modified: "2024-11-15",
          type: "file",
          path: path.join("/") + "/service_contract_2024_ABC.pdf"
        },
        {
          id: "f2",
          name: "invoice_november_034_office_supplies.pdf",
          size: "1.8 MB",
          modified: "2024-11-14",
          type: "file",
          path: path.join("/") + "/invoice_november_034_office_supplies.pdf"
        },
        {
          id: "f3",
          name: "receipt_coffee_corner_transaction.jpg",
          size: "456 KB",
          modified: "2024-11-12",
          type: "file",
          path: path.join("/") + "/receipt_coffee_corner_transaction.jpg"
        },
        {
          id: "f4",
          name: "quarterly_financial_report_Q3_2024.pdf",
          size: "892 KB",
          modified: "2024-11-11",
          type: "file",
          path: path.join("/") + "/quarterly_financial_report_Q3_2024.pdf"
        },
        {
          id: "f5",
          name: "partnership_agreement_tech_ventures.docx",
          size: "678 KB",
          modified: "2024-11-09",
          type: "file",
          path: path.join("/") + "/partnership_agreement_tech_ventures.docx"
        },
        {
          id: "f6",
          name: "purchase_order_invoice_789_equipment.pdf",
          size: "1.2 MB",
          modified: "2024-11-10",
          type: "file",
          path: path.join("/") + "/purchase_order_invoice_789_equipment.pdf"
        },
        {
          id: "f7",
          name: "scan_document_unclassified_mixed.pdf",
          size: "234 KB",
          modified: "2024-11-08",
          type: "file",
          path: path.join("/") + "/scan_document_unclassified_mixed.pdf"
        },
        {
          id: "f8",
          name: "business_contract_final_signed.pdf",
          size: "1.9 MB",
          modified: "2024-11-06",
          type: "file",
          path: path.join("/") + "/business_contract_final_signed.pdf"
        }
      ];
    }
  }

  static createFileFromCloudItem(cloudFile: CloudFile): File {
    const content = "";
    return new File([content], cloudFile.name, {
      type: this.getMimeType(cloudFile.name),
      lastModified: new Date(cloudFile.modified).getTime()
    });
  }
}

export class CategoryUtils {
  static generateCategoryId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  static calculateClassificationSummary(categories: any[]) {
    const totalDocuments = categories.reduce(
      (sum: number, cat: any) => sum + cat.count,
      0
    );

    return {
      totalDocuments,
      totalCategories: categories.length,
      categories,
      averageConfidence: 0.87,
      classificationDate: new Date(),
      processingTime: 1250
    };
  }

  static getColorForCategory(index: number): string {
    const colors = [
      "#2563eb",
      "#059669",
      "#7c3aed",
      "#ea580c",
      "#dc2626",
      "#0891b2",
      "#7c2d12",
      "#be123c"
    ];
    return colors[index % colors.length];
  }
}
